import React, { useCallback, useState } from 'react';
import { DragPreview } from '../drag-preview/DragPreview';
import { DragPreviewModel } from '@/types/admin/common';
import { ReactComponent as DragIcon } from '@/assets/icons/dragger.svg';
import './DraggableListItem.scss';

export interface DraggableListItemProps<TEntity> {
    entity: TEntity;
    id: number | string;
    ariaLabel: string;
    renderEntityComponent: (entity: TEntity) => React.ReactNode;
    entities: TEntity[];
    idSelector: (entity: TEntity) => number | string;
    onEntitiesReordered: (entities: TEntity[]) => void;
    reorderDisabled?: boolean;
    hideDragHandle?: boolean;
}

export const DraggableListItem = <TEntity,>({
    entity,
    id,
    renderEntityComponent,
    ariaLabel,
    entities,
    idSelector,
    onEntitiesReordered,
    reorderDisabled = false,
    hideDragHandle = false,
}: DraggableListItemProps<TEntity>) => {
    const emptyDragImage = 'data:image/gif;base64,R0lGODlhAQABAIAAAAUEBAAAACwAAAAAAQABAAACAkQBADs=';
    const [dragPreview, setDragPreview] = useState<DragPreviewModel<TEntity>>({
        visible: false,
        x: 0,
        y: 0,
        item: null,
    });

    const hideDragPreview = useCallback(() => {
        setDragPreview({
            visible: false,
            x: 0,
            y: 0,
            item: null,
        });
    }, []);

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        if (reorderDisabled || hideDragHandle) {
            return;
        }

        e.preventDefault();
    };

    const handleDrop = useCallback(
        (e: React.DragEvent<HTMLDivElement>, id: number | string) => {
            if (reorderDisabled || hideDragHandle) {
                return;
            }

            e.preventDefault();
            hideDragPreview();

            const draggedIdStr = e.dataTransfer.getData('text/plain');
            const draggedId = isNaN(Number(draggedIdStr)) ? draggedIdStr : Number(draggedIdStr);
            if (draggedId === id) return;

            const updatedEntities = [...entities];
            const fromIndex = updatedEntities.findIndex((m) => idSelector(m) === draggedId);
            const toIndex = updatedEntities.findIndex((m) => idSelector(m) === id);

            const [draggedItem] = updatedEntities.splice(fromIndex, 1);
            updatedEntities.splice(toIndex, 0, draggedItem);

            onEntitiesReordered(updatedEntities);
        },
        [entities, idSelector, onEntitiesReordered, reorderDisabled, hideDragHandle, hideDragPreview],
    );

    const handleDragEnd = () => {
        hideDragPreview();
    };

    const handleDragStart = useCallback(
        (e: React.DragEvent<HTMLDivElement>, id: number | string) => {
            if (reorderDisabled || hideDragHandle) {
                e.preventDefault();
                return;
            }

            const entity = entities.find((x) => idSelector(x) === id);
            if (!entity) return;

            e.dataTransfer.setData('text/plain', id.toString());

            setDragPreview({
                visible: true,
                x: e.clientX,
                y: e.clientY,
                item: entity,
            });

            const dragImage = new Image();
            dragImage.src = emptyDragImage;
            e.dataTransfer.setDragImage(dragImage, 0, 0);
        },
        [entities, idSelector, reorderDisabled, hideDragHandle],
    );

    const handleDrag = (e: React.DragEvent<HTMLDivElement>) => {
        if (e.clientX !== 0 && e.clientY !== 0) {
            setDragPreview((prev) => ({
                ...prev,
                x: e.clientX,
                y: e.clientY,
            }));
        }
    };

    return (
        <div
            className="draggable-item-wrapper"
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, id)}
            onDragEnd={handleDragEnd}
        >
            <DragPreview entity={entity} dragPreview={dragPreview} renderEntityComponent={renderEntityComponent} />
            <div className={'draggable-item'} key={id}>
                {!hideDragHandle && (
                    <div
                        className="dragger"
                        draggable={!reorderDisabled}
                        onDragStart={(e) => handleDragStart(e, id)}
                        onDrag={handleDrag}
                        onDragEnd={handleDragEnd}
                        role="button"
                        aria-label={ariaLabel}
                        tabIndex={reorderDisabled ? -1 : 0}
                    >
                        <DragIcon />
                    </div>
                )}
                <div className="item-data">{renderEntityComponent(entity)}</div>
            </div>
        </div>
    );
};
